import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import classNames from 'classnames';

import BaseSelectField from 'components/essentials/BaseSelectField';
import { PDP_READYTOSHIP_TAB_ID } from 'commons/constants';
import usePdpSelectedCustomizerTab from '../state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePDPOptions from '../state/context/usePDPOptions';

const Option = props => {
  const { hasRightValue, rightFieldName } = props;
  if (!hasRightValue)
    return (
      <div className="text-sm font-bold text-[#333] flex border-gray-light6 cursor-pointer relative hover:border-gray-light1 hover:shadow-none">
        <components.Option {...props} />
      </div>
    );
  const selectedValue = props.getValue();
  return (
    <div className="flex flex-row items-center justify-between">
      <components.Option {...props} />
      <span>{selectedValue[rightFieldName]}</span>
    </div>
  );
};
Option.propTypes = {
  hasRightValue: PropTypes.bool,
  rightFieldName: PropTypes.string,
  getValue: PropTypes.func,
};

const SingleValue = props => {
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const { stockDisabledStatus = {}, getValue } = props;
  const selectedValue = getValue();

  const finalValue = selectedValue
    ? Array.isArray(selectedValue)
      ? selectedValue[0]
      : selectedValue
    : {};

  const isDisabled =
    pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID &&
    stockDisabledStatus[finalValue?.value];

  return (
    <div
      className={classNames({
        'cursor-not-allowed [&_.react-select\\_\\_single-value]:text-gray-light4': isDisabled,
      })}
    >
      <components.SingleValue isDisabled={isDisabled} {...props} />
    </div>
  );
};

SingleValue.propTypes = {
  stockDisabledStatus: PropTypes.object,
  getValue: PropTypes.func,
};

const PDPTextDropdownOptionGroup = ({
  optionId,
  hasRightValue = false,
  rightFieldName = '',
}) => {
  const { data = {}, actions } = usePDPOptions();
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const [selectedOption, setSelectedOption] = useState(null);

  const { options = [], selectedOptions = {}, stock = {} } = data;
  const { stockOptions = [], stockOptionsDisabledStatus = {} } = stock;

  const values = useMemo(() => {
    const finalOptions =
      pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID
        ? stockOptions
        : options;

    if (!finalOptions || !optionId || !selectedOptions) {
      return [];
    }

    const selectedOptionVal = finalOptions.find(
      option => option.id === optionId
    );

    if (!selectedOptionVal) {
      return [];
    }

    const updatedValues = selectedOptionVal.values.map(optionValue => ({
      value: optionValue.id,
      label: optionValue.value.trim(),
      rightValue: `$${optionValue.additional_cost}`,
      isDisabled:
        pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID &&
        stockOptionsDisabledStatus[optionValue.id],
    }));
    return updatedValues;
  }, [
    options,
    stockOptions,
    stockOptionsDisabledStatus,
    selectedOptions,
    pdpSelectedCustomizerTab,
    optionId,
  ]);

  const defaultSelectionValue = useMemo(() => {
    if (!values?.length || !data?.selectedOptionsExtended) {
      return {};
    }

    return (
      values.find(
        val =>
          `${val.value}` ===
          `${data.selectedOptionsExtended?.[optionId]?.value?.id}`
      ) || {}
    );
  }, [values, data?.selectedOptionsExtended, optionId]);

  useEffect(() => {
    if (!selectedOptions || !values.length) {
      return;
    }
    const newSelOption = values.find(
      val => `${val.value}` === `${selectedOptions?.[optionId]}`
    );

    if (
      !selectedOption ||
      (newSelOption && newSelOption.value !== selectedOption.value)
    ) {
      setSelectedOption(newSelOption);
    }
  }, [selectedOptions, values, optionId, selectedOption]);

  const handleChange = optionValue => {
    actions.onOptionValueSelection(optionId, optionValue.value);
  };

  const optionName = options?.find(option => option.id === optionId)?.name;

  return (
    <div className="flex flex-col w-full">
      <div className="text-sm w-full font-bold text-gray-dark p-0 mt-0 mx-0 mb-1.5">
        {optionName ? `Select ${optionName}` : ''}
      </div>

      <div>
        {values.length ? (
          <BaseSelectField
            // We use a custom className here to support overriding nested
            // third party styles in globals-tailwind.css
            className="pdp-dropdown-field"
            options={values}
            onChange={handleChange}
            value={selectedOption}
            customComponents={{
              // eslint-disable-next-line
              Option: props => (
                <Option
                  hasRightValue={hasRightValue}
                  rightFieldName={rightFieldName}
                  {...props}
                />
              ),
              // eslint-disable-next-line
              SingleValue: props => (
                <SingleValue
                  stockDisabledStatus={stock?.stockOptionsDisabledStatus}
                  {...props}
                />
              ),
            }}
            defaultValue={defaultSelectionValue}
            ariaLabel="Options Dropdown"
          />
        ) : null}
      </div>
    </div>
  );
};

PDPTextDropdownOptionGroup.propTypes = {
  optionId: PropTypes.number,
  hasRightValue: PropTypes.bool,
  rightFieldName: PropTypes.string,
};

export default PDPTextDropdownOptionGroup;
